import React, { useEffect, useMemo, useState } from "react";
import { BellRing, Clock3, CreditCard, Edit2, Loader2, PackagePlus, Save, Trash2, X } from "lucide-react";
import ConfirmationModal from "../components/CommonComponents/ConfirmationModal";
import MainLayout from "../components/CommonComponents/MainLayout";
import {
  createSubscriptionPackage,
  deleteSubscriptionPackage,
  fetchSubscriptionPackages,
  updateSubscriptionPackage,
} from "../services/subscriptionPackageApi";
import { fetchSubscriptionSettings, updateSubscriptionSettings } from "../services/subscriptionSettingsApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { tableBase, tableCell, tableContainer, tableHead, tableHeader, tableRow } from "../utils/tableStyle";

const emptyForm = {
  code: "",
  name: "",
  base_monthly_amount: 1699,
  additional_user_monthly_amount: 150,
  included_users_per_active_branch: 2,
  yearly_enabled: false,
  yearly_discount_type: "PERCENTAGE",
  yearly_discount_value: 0,
  status: "ACTIVE",
};

const defaultReminderSettings = {
  base_monthly_amount: 1699,
  additional_user_monthly_amount: 150,
  included_users_per_active_branch: 2,
  subscription_reminder_enabled: true,
  subscription_reminder_days_before: 5,
  subscription_reminder_interval_hours: 6,
};

const peso = (value) => `₱${Number(value || 0).toLocaleString()}`;
const roundedPeso = (value) => peso(Math.round(Number(value || 0)));

const calculateYearlyPreview = (monthlyAmount, discountType, discountValue) => {
  const monthly = Number(monthlyAmount || 0);
  const discount = Number(discountValue || 0);
  const discountedMonthly = discountType === "FIXED"
    ? Math.max(monthly - discount, 0)
    : Math.max(monthly - (monthly * Math.min(discount, 100) / 100), 0);
  return discountedMonthly * 12;
};

export default function SubscriptionSettingsPage() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingPackage, setEditingPackage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reminderSettings, setReminderSettings] = useState(defaultReminderSettings);
  const [loadingReminderSettings, setLoadingReminderSettings] = useState(false);
  const [savingReminderSettings, setSavingReminderSettings] = useState(false);

  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => String(a.code).localeCompare(String(b.code))),
    [packages]
  );

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await fetchSubscriptionPackages();
      setPackages(response.data?.data || []);
    } catch (error) {
      console.error("Failed to load subscription packages:", error);
      showErrorToast("Failed to load subscription packages.");
    } finally {
      setLoading(false);
    }
  };

  const loadReminderSettings = async () => {
    setLoadingReminderSettings(true);
    try {
      const response = await fetchSubscriptionSettings();
      setReminderSettings({ ...defaultReminderSettings, ...(response.data || {}) });
    } catch (error) {
      console.error("Failed to load subscription reminder settings:", error);
      showErrorToast("Failed to load subscription reminder settings.");
    } finally {
      setLoadingReminderSettings(false);
    }
  };

  useEffect(() => {
    loadPackages();
    loadReminderSettings();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingPackage(null);
  };

  const handleEdit = (subscriptionPackage) => {
    setEditingPackage(subscriptionPackage);
    setForm({
      code: subscriptionPackage.code || "",
      name: subscriptionPackage.name || "",
      base_monthly_amount: subscriptionPackage.base_monthly_amount ?? 0,
      additional_user_monthly_amount: subscriptionPackage.additional_user_monthly_amount ?? 0,
      included_users_per_active_branch: subscriptionPackage.included_users_per_active_branch ?? 0,
      yearly_enabled: Boolean(subscriptionPackage.yearly_enabled),
      yearly_discount_type: subscriptionPackage.yearly_discount_type || "PERCENTAGE",
      yearly_discount_value: subscriptionPackage.yearly_discount_value ?? 0,
      status: subscriptionPackage.status || "ACTIVE",
    });
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleReminderChange = (event) => {
    const { name, type, checked, value } = event.target;
    setReminderSettings((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleReminderSubmit = async (event) => {
    event.preventDefault();
    setSavingReminderSettings(true);
    try {
      const payload = {
        ...reminderSettings,
        base_monthly_amount: Number(reminderSettings.base_monthly_amount),
        additional_user_monthly_amount: Number(reminderSettings.additional_user_monthly_amount),
        included_users_per_active_branch: Number(reminderSettings.included_users_per_active_branch),
        subscription_reminder_enabled: Boolean(reminderSettings.subscription_reminder_enabled),
        subscription_reminder_days_before: Number(reminderSettings.subscription_reminder_days_before),
        subscription_reminder_interval_hours: Number(reminderSettings.subscription_reminder_interval_hours),
      };
      const response = await updateSubscriptionSettings(payload);
      setReminderSettings({ ...defaultReminderSettings, ...(response.data?.settings || payload) });
      showSuccessToast("Subscription reminder settings saved.");
    } catch (error) {
      console.error("Failed to save subscription reminder settings:", error);
      showErrorToast(error.response?.data?.message || "Failed to save subscription reminder settings.");
    } finally {
      setSavingReminderSettings(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: String(form.code).trim().toUpperCase(),
        name: String(form.name).trim(),
        base_monthly_amount: Number(form.base_monthly_amount),
        additional_user_monthly_amount: Number(form.additional_user_monthly_amount),
        included_users_per_active_branch: Number(form.included_users_per_active_branch),
        yearly_enabled: Boolean(form.yearly_enabled),
        yearly_discount_type: String(form.yearly_discount_type || "PERCENTAGE").toUpperCase(),
        yearly_discount_value: Number(form.yearly_discount_value || 0),
      };

      if (editingPackage) {
        await updateSubscriptionPackage(editingPackage.id, payload);
        showSuccessToast("Subscription package updated.");
      } else {
        await createSubscriptionPackage(payload);
        showSuccessToast("Subscription package created.");
      }

      resetForm();
      loadPackages();
    } catch (error) {
      console.error("Failed to save subscription package:", error);
      showErrorToast(error.response?.data?.message || "Failed to save subscription package.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSubscriptionPackage(deleteTarget.id);
      showSuccessToast("Subscription package deleted.");
      setDeleteTarget(null);
      loadPackages();
    } catch (error) {
      console.error("Failed to delete subscription package:", error);
      showErrorToast(error.response?.data?.message || "Failed to delete subscription package.");
    }
  };

  return (
    <MainLayout>
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-7 px-4 py-8" style={{ background: "var(--color-hover)" }}>
        <section className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--color-muted)" }}>
              <CreditCard className="h-4 w-4" />
              SaaS Billing
            </div>
            <h1 className="mt-4 text-4xl font-bold tracking-tight" style={{ color: "var(--color-text)" }}>Subscription Packages</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6" style={{ color: "var(--color-muted)" }}>
              Create package codes that customers can use for monthly POS billing, included staff allowance, and add-on user pricing.
            </p>
          </div>
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm font-semibold shadow-sm transition"
            style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
          >
            <PackagePlus className="h-4 w-4" />
            New Package
          </button>
        </section>

        <form
          onSubmit={handleReminderSubmit}
          className="rounded-lg border p-5 shadow-sm"
          style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}
        >
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--color-muted)" }}>
                <BellRing className="h-4 w-4" />
                Renewal Notices
              </div>
              <h2 className="mt-3 text-base font-semibold" style={{ color: "var(--color-text)" }}>Subscription Email Reminders</h2>
            </div>
            <label className="inline-flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3" style={{ borderColor: "var(--color-border)", background: "var(--color-hover)" }}>
              <input
                name="subscription_reminder_enabled"
                type="checkbox"
                checked={Boolean(reminderSettings.subscription_reminder_enabled)}
                onChange={handleReminderChange}
                disabled={loadingReminderSettings || savingReminderSettings}
                className="h-4 w-4"
                style={{ accentColor: "var(--color-primary)" }}
              />
              <span className="text-sm font-semibold" style={{ color: "var(--color-text)" }}>
                {reminderSettings.subscription_reminder_enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <fieldset disabled={loadingReminderSettings || savingReminderSettings} className="grid gap-4 md:grid-cols-[1fr,1fr,auto] disabled:opacity-70">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Advance Reminder Days</span>
              <div className="relative">
                <BellRing className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "var(--color-muted)" }} />
                <input
                  name="subscription_reminder_days_before"
                  type="number"
                  min="1"
                  max="60"
                  step="1"
                  value={reminderSettings.subscription_reminder_days_before}
                  onChange={handleReminderChange}
                  className="w-full rounded-md border py-3 pl-11 pr-4 text-sm outline-none"
                  style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  required
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Check Interval Hours</span>
              <div className="relative">
                <Clock3 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: "var(--color-muted)" }} />
                <input
                  name="subscription_reminder_interval_hours"
                  type="number"
                  min="1"
                  max="24"
                  step="1"
                  value={reminderSettings.subscription_reminder_interval_hours}
                  onChange={handleReminderChange}
                  className="w-full rounded-md border py-3 pl-11 pr-4 text-sm outline-none"
                  style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  required
                />
              </div>
            </label>
            <button type="submit" disabled={loadingReminderSettings || savingReminderSettings} className="btn-primary inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-wait md:self-end">
              {savingReminderSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Reminder
            </button>
          </fieldset>
        </form>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border p-5 shadow-sm"
          style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold" style={{ color: "var(--color-text)" }}>
              {editingPackage ? `Edit ${editingPackage.code}` : "Add Subscription Package"}
            </h2>
            {editingPackage && (
              <button type="button" onClick={resetForm} className="rounded-md p-2" style={{ color: "var(--color-muted)" }} aria-label="Cancel edit">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <fieldset disabled={saving} className="grid gap-4 md:grid-cols-2 xl:grid-cols-7 disabled:opacity-70">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Code</span>
              <input name="code" value={form.code} onChange={handleChange} placeholder="SUB-001" className="w-full rounded-md border px-4 py-3 text-sm uppercase outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} required />
            </label>
            <label className="block md:col-span-2">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Package Name</span>
              <input name="name" value={form.name} onChange={handleChange} placeholder="Retail POS Starter" className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} required />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Monthly</span>
              <input name="base_monthly_amount" type="number" min="0" step="0.01" value={form.base_monthly_amount} onChange={handleChange} className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} required />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Add-on User</span>
              <input name="additional_user_monthly_amount" type="number" min="0" step="0.01" value={form.additional_user_monthly_amount} onChange={handleChange} className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} required />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Included/Branch</span>
              <input name="included_users_per_active_branch" type="number" min="0" step="1" value={form.included_users_per_active_branch} onChange={handleChange} className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} required />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Status</span>
              <select name="status" value={form.status} onChange={handleChange} className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </label>
            <div className="rounded-md border p-4 md:col-span-2 xl:col-span-7" style={{ borderColor: "var(--color-border)", background: "color-mix(in srgb, var(--color-primary) 4%, var(--color-bg))" }}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    name="yearly_enabled"
                    type="checkbox"
                    checked={form.yearly_enabled}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4"
                    style={{ accentColor: "var(--color-primary)" }}
                  />
                  <span>
                    <span className="block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Offer Yearly Subscription</span>
                    <span className="mt-1 block text-xs" style={{ color: "var(--color-muted)" }}>
                      Yearly price is calculated from the customer monthly bill, then discounted.
                    </span>
                  </span>
                </label>
                {form.yearly_enabled && (
                  <div className="rounded-full border px-3 py-1 text-xs font-semibold" style={{ borderColor: "var(--color-border)", color: "var(--color-text)", background: "var(--color-bg)" }}>
                    Base yearly preview: {roundedPeso(calculateYearlyPreview(form.base_monthly_amount, form.yearly_discount_type, form.yearly_discount_value))}
                  </div>
                )}
              </div>

              {form.yearly_enabled && (
                <div className="mt-4 grid gap-4 md:grid-cols-[220px,1fr]">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Discount Type</span>
                    <select name="yearly_discount_type" value={form.yearly_discount_type} onChange={handleChange} className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}>
                      <option value="PERCENTAGE">Percentage</option>
                      <option value="FIXED">Fixed amount</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>
                      {form.yearly_discount_type === "FIXED" ? "Fixed Monthly Discount" : "Percentage Discount"}
                    </span>
                    <input
                      name="yearly_discount_value"
                      type="number"
                      min="0"
                      max={form.yearly_discount_type === "PERCENTAGE" ? "100" : undefined}
                      step="0.01"
                      value={form.yearly_discount_value}
                      onChange={handleChange}
                      className="w-full rounded-md border px-4 py-3 text-sm outline-none"
                      style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                    />
                    <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
                      {form.yearly_discount_type === "FIXED"
                        ? "Deducts this amount from the monthly bill before multiplying by 12."
                        : "Deducts this percent from the monthly bill before multiplying by 12."}
                    </p>
                  </label>
                </div>
              )}
            </div>
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-wait md:col-span-2 xl:col-span-1 xl:self-end">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editingPackage ? "Save" : "Add"}
            </button>
          </fieldset>
        </form>

        <div className={`${tableContainer} shadow-sm`} style={{ border: "1px solid var(--color-border)" }}>
          <table className={tableBase}>
            <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
              <tr>
                <th className={tableHeader + " text-left"}>Code</th>
                <th className={tableHeader + " text-left"}>Name</th>
                <th className={tableHeader + " text-left"}>Monthly</th>
                <th className={tableHeader + " text-left"}>Yearly</th>
                <th className={tableHeader + " text-left"}>Add-on User</th>
                <th className={tableHeader + " text-left"}>Included</th>
                <th className={tableHeader + " text-left"}>Status</th>
                <th className={tableHeader + " text-right"}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className={tableCell + " py-8 text-center"} style={{ color: "var(--color-muted)" }}>Loading packages...</td>
                </tr>
              )}
              {!loading && sortedPackages.map((subscriptionPackage) => (
                <tr key={subscriptionPackage.id} className={tableRow + " border-b last:border-0"} style={{ borderColor: "var(--color-border)" }}>
                  <td className={tableCell + " font-semibold"} style={{ color: "var(--color-text)" }}>
                    <div>{subscriptionPackage.code}</div>
                    {Number(subscriptionPackage.active_customer_count || 0) > 0 && (
                      <div className="mt-1 text-xs font-medium" style={{ color: "var(--color-muted)" }}>
                        Used by {subscriptionPackage.active_customer_count} active customer{Number(subscriptionPackage.active_customer_count) === 1 ? "" : "s"}
                      </div>
                    )}
                  </td>
                  <td className={tableCell} style={{ color: "var(--color-text)" }}>{subscriptionPackage.name}</td>
                  <td className={tableCell} style={{ color: "var(--color-text)" }}>{peso(subscriptionPackage.base_monthly_amount)}</td>
                  <td className={tableCell} style={{ color: "var(--color-text)" }}>
                    {subscriptionPackage.yearly_enabled ? (
                      <div className="space-y-1">
                        <div className="font-semibold">
                          {roundedPeso(calculateYearlyPreview(
                            subscriptionPackage.base_monthly_amount,
                            subscriptionPackage.yearly_discount_type,
                            subscriptionPackage.yearly_discount_value
                          ))}
                        </div>
                        <div className="text-xs" style={{ color: "var(--color-muted)" }}>
                          {subscriptionPackage.yearly_discount_type === "FIXED"
                            ? `${peso(subscriptionPackage.yearly_discount_value)} off monthly`
                            : `${Number(subscriptionPackage.yearly_discount_value || 0)}% off`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs" style={{ color: "var(--color-muted)" }}>Not offered</span>
                    )}
                  </td>
                  <td className={tableCell} style={{ color: "var(--color-text)" }}>{peso(subscriptionPackage.additional_user_monthly_amount)}/month</td>
                  <td className={tableCell} style={{ color: "var(--color-text)" }}>{subscriptionPackage.included_users_per_active_branch} users / active branch</td>
                  <td className={tableCell}>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${subscriptionPackage.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {subscriptionPackage.status}
                    </span>
                  </td>
                  <td className={tableCell}>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => handleEdit(subscriptionPackage)} className="rounded-md border p-2 shadow-sm" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }} aria-label={`Edit ${subscriptionPackage.code}`}>
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(subscriptionPackage)}
                        disabled={Number(subscriptionPackage.active_customer_count || 0) > 0}
                        className="rounded-md border border-rose-200 bg-rose-50 p-2 text-rose-600 shadow-sm hover:bg-rose-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                        aria-label={`Delete ${subscriptionPackage.code}`}
                        title={Number(subscriptionPackage.active_customer_count || 0) > 0 ? "Reassign or deactivate active customers before deleting this package." : `Delete ${subscriptionPackage.code}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && sortedPackages.length === 0 && (
                <tr>
                  <td colSpan="8" className={tableCell + " py-8 text-center"} style={{ color: "var(--color-muted)" }}>No subscription packages yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Subscription Package"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.code}? This removes it from the available subscription packages.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="delete"
      />
    </MainLayout>
  );
}
